import type { ReactNode } from 'react';

import { cn } from '@ideoz/ui';

/**
 * Shared illustration for empty lists. Purely decorative, uses design tokens
 * so it adapts to light/dark automatically.
 */
export function EmptyIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 220 150"
      role="presentation"
      aria-hidden
      className={cn('h-auto w-[220px]', className)}
    >
      {/* soft halo */}
      <ellipse cx="110" cy="128" rx="78" ry="10" className="fill-blue-500/10" />

      {/* back card */}
      <rect
        x="42"
        y="26"
        width="136"
        height="72"
        rx="10"
        className="fill-gray-50 stroke-border dark:fill-gray-900 dark:stroke-gray-800"
        strokeWidth="1.5"
      />
      {/* mid card */}
      <rect
        x="34"
        y="44"
        width="152"
        height="72"
        rx="12"
        className="fill-background stroke-border"
        strokeWidth="1.5"
      />
      {/* placeholder rows */}
      <rect x="50" y="62" width="70" height="7" rx="3.5" className="fill-blue-500/25" />
      <rect x="50" y="78" width="112" height="6" rx="3" className="fill-border" />
      <rect x="50" y="92" width="86" height="6" rx="3" className="fill-border" />

      {/* plus badge */}
      <circle cx="172" cy="104" r="17" className="fill-blue-500" />
      <path
        d="M172 96.5v15M164.5 104h15"
        className="stroke-primary-25"
        strokeWidth="2.4"
        strokeLinecap="round"
      />

      {/* sparkles */}
      <path
        d="M32 24l2.4 5.6L40 32l-5.6 2.4L32 40l-2.4-5.6L24 32l5.6-2.4z"
        className="fill-warning-500"
      />
      <path
        d="M190 34l1.6 3.7L195 39.3l-3.4 1.6L190 44.6l-1.6-3.7L185 39.3l3.4-1.6z"
        className="fill-blue-500/50"
      />
    </svg>
  );
}

export function EmptyState({
  title,
  description,
  action,
  secondaryAction,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-2xl border border-dashed border-border bg-gray-50 dark:bg-gray-900 px-6 py-14 text-center',
        className
      )}
    >
      <EmptyIllustration />
      <h3 className="mt-6 text-base font-semibold tracking-[-0.01em] text-gray-900 dark:text-gray-100">
        {title}
      </h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
